import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { IoTrashOutline } from "react-icons/io5";
import { ChevronLeft, X, Maximize2, Minimize2, ChevronDown, Check } from "lucide-react";

import "../CSS/IssueForm.css";
import "../CSS/Message.css";
import IssueLoaderButton from "./pageLoaders/IssueLoaderButton";
import PageLoader from "./pageLoaders/PageLoader";

// Helper function to resolve field display label with priority: labelForCustomer -> labelForAgent -> label -> apiName
const getFieldDisplayLabel = (field) => {
  if (!field) return "";
  
  const agentLabel = typeof field.labelForAgent === "string" ? field.labelForAgent.trim() : "";
  if (agentLabel) return agentLabel;
  
  const customerLabel = typeof field.labelForCustomer === "string" ? field.labelForCustomer.trim() : "";
  if (customerLabel) return customerLabel;
  const defaultLabel = typeof field.label === "string" ? field.label.trim() : "";
  if (defaultLabel) return defaultLabel;

  return field.apiName || "";
};

// Helper function to find a field by globalFieldId in canvas sections
const findFieldByGlobalId = (sections, globalFieldId) => {
  for (const sec of sections) {
    for (const row of sec.rows || []) {
      if (row.left?.globalFieldId === globalFieldId) return row.left;
      if (row.right?.globalFieldId === globalFieldId) return row.right;
    }
  }
  return null;
};

// Helper function to calculate field visibility, required, and disabled state based strictly on dependencies
const getFieldState = (field, sections, currentFormData) => {
  const state = {
    visible: true,
    required: Boolean(field.mandatory || field.required),
    disabled: false, // Readonly/disabled is driven strictly by dependency actions if present
  };

  if (
    !field.dependencies ||
    !field.dependencies.dependents ||
    field.dependencies.dependents.length === 0
  ) {
    return state;
  }

  // Check if there are any 'show' rules - if yes, default to hidden until rule matches
  const hasShowRules = field.dependencies.dependents.some(
    (rule) => rule.action === "show"
  );
  if (hasShowRules) {
    state.visible = false;
  }

  field.dependencies.dependents.forEach((rule) => {
    const triggerField = findFieldByGlobalId(sections, rule.triggerFieldId);
    if (!triggerField) return;

    const currentValue = currentFormData[triggerField.apiName];

    let isMatch = false;
    if (rule.conditionType === "equals") {
      isMatch = String(currentValue) === String(rule.triggerValue);
    } else if (rule.conditionType === "not_equals") {
      isMatch = String(currentValue) !== String(rule.triggerValue);
    } else if (rule.conditionType === "contains") {
      isMatch = String(currentValue || "").includes(String(rule.triggerValue));
    } else if (rule.conditionType === "empty") {
      isMatch =
        !currentValue ||
        currentValue === "" ||
        currentValue === null ||
        currentValue === undefined;
    } else if (rule.conditionType === "not_empty") {
      isMatch =
        currentValue !== "" &&
        currentValue !== null &&
        currentValue !== undefined;
    }

    if (isMatch) {
      switch (rule.action) {
        case "show":
          state.visible = true;
          break;
        case "hide":
          state.visible = false;
          break;
        case "require":
          state.required = true;
          break;
        case "optional":
          state.required = false;
          break;
        case "enable":
          state.disabled = false;
          break;
        case "disable":
          state.disabled = true;
          break;
        default:
          break;
      }
    }
  });

  return state;
};

// Custom Select Component supporting option colors and enhanced option badges
const EnhancedSelect = ({ field, value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Merge options and enhancedOptions
  let rawOptions = field.options || [];
  let enhancedOptions = field.enhancedOptions || [];

  let optionsList = [];
  if (enhancedOptions.length > 0) {
    optionsList = enhancedOptions;
  } else if (rawOptions.length > 0) {
    optionsList = rawOptions.map((opt) =>
      typeof opt === "object" ? opt : { value: opt, label: opt }
    );
  }

  // Filter disabled options
  if (field.disabledOptions && field.disabledOptions.length > 0) {
    optionsList = optionsList.filter(
      (opt) => !field.disabledOptions.includes(String(opt.value))
    );
  }

  const selectedOpt = optionsList.find((opt) => String(opt.value) === String(value));
  const fieldLabel = getFieldDisplayLabel(field);

  const handleSelect = (val) => {
    if (disabled) return;
    onChange({ target: { value: val } });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm font-normal text-slate-800 flex items-center justify-between transition-all cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed shadow-2xs"
      >
        <div className="flex items-center gap-2 truncate">
          {selectedOpt ? (
            <>
              {selectedOpt.color && (
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: selectedOpt.color }}
                />
              )}
              <span className="truncate">{selectedOpt.label || selectedOpt.value}</span>
              {selectedOpt.statusGroup && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded tracking-wider shrink-0"
                  style={{
                    backgroundColor: `${selectedOpt.color || "#3b82f6"}15`,
                    color: selectedOpt.color || "#3b82f6",
                    border: `1px solid ${selectedOpt.color || "#3b82f6"}30`,
                  }}
                >
                  {selectedOpt.statusGroup.replace(/_/g, " ")}
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-400">
              {field.placeholder || `Select ${fieldLabel}`}
            </span>
          )}
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto p-1 text-xs sm:text-sm dexkor-scrollbar">
          <div
            onClick={() => handleSelect("")}
            className="px-3 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-slate-400 font-normal"
          >
            Select {fieldLabel}
          </div>
          {optionsList.map((opt, idx) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <div
                key={idx}
                onClick={() => handleSelect(opt.value)}
                className={`px-3 py-1.5 rounded-md cursor-pointer flex items-center justify-between transition-colors ${
                  isSelected ? "bg-indigo-50/70 text-indigo-950 font-medium" : "hover:bg-slate-50 text-slate-700"
                }`}
              >
                <div className="flex items-center gap-2 truncate">
                  {opt.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: opt.color }}
                    />
                  )}
                  <span className="truncate">{opt.label || opt.value}</span>
                  {opt.statusGroup && (
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded tracking-wider shrink-0"
                      style={{
                        backgroundColor: `${opt.color || "#3b82f6"}15`,
                        color: opt.color || "#3b82f6",
                        border: `1px solid ${opt.color || "#3b82f6"}30`,
                      }}
                    >
                      {opt.statusGroup.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                {isSelected && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const IssueFormV2 = ({
  onSubmit,
  setShowForm,
  setTicketId,
  ticketId,
  loading,
  setLoading,
  widgetConfigs,
  setupDetails,
  isExpanded,
  setIsExpanded,
  setIsOpen,
}) => {
  const [formData, setFormData] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [partnerUsers, setPartnerUsers] = useState([]);

  // Forms state
  const [availableForms, setAvailableForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loadingForms, setLoadingForms] = useState(true);
  const [canvasSections, setCanvasSections] = useState([]);

  useEffect(() => {
    const details = JSON.parse(
      localStorage.getItem("dexkor-chat-widget-customer-details") || "null"
    );
    setCustomerDetails(details);
  }, []);

  useEffect(() => {
    if (ticketId) {
      onSubmit({ ...formData });
    }
  }, [ticketId]);

  // Load partner users for user type fields (assignee, owner, etc.)
  useEffect(() => {
    const fetchPartnerUsers = async () => {
      if (!widgetConfigs?.uniquePartnerId) return;
      try {
        const authUrl =
          process.env.REACT_APP_AUTH_MICROSERVICE_URL ||
          (process.env.REACT_APP_CHAT_MICROSERVICE_URL || "").replace(/\/api\/v1\/?$/, "");

        const res = await axios.get(`${authUrl}/users/all/${widgetConfigs.uniquePartnerId}`);
        const users = res.data?.users || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setPartnerUsers(users);
      } catch (err) {
        console.error("Error fetching partner users for widget form:", err);
      }
    };
    fetchPartnerUsers();
  }, [widgetConfigs?.uniquePartnerId]);

  // Load active ticket forms for partner filtered by showOnWidget === true
  const loadAvailableForms = useCallback(async () => {
    if (!widgetConfigs?.uniquePartnerId) {
      setLoadingForms(false);
      return;
    }
    try {
      setLoadingForms(true);
      const token = localStorage.getItem("dexkor-chat-widget-auth-token");
      const baseUrl = process.env.REACT_APP_CHAT_MICROSERVICE_URL || "";

      const response = await axios.get(
        `${baseUrl}/form/tickets/${widgetConfigs.uniquePartnerId}`,
        {
          params: {
            showOnWidget: true,
            isActive: true,
          },
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      let forms = response.data?.forms || response.data?.data || [];
      if (!Array.isArray(forms) && response.data) {
        forms = Array.isArray(response.data) ? response.data : [];
      }

      // Fallback: If no form explicitly marked showOnWidget for this partner, load active forms
      if (forms.length === 0) {
        const fallbackResponse = await axios.get(
          `${baseUrl}/form/tickets/${widgetConfigs.uniquePartnerId}`,
          {
            params: {
              isActive: true,
            },
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );
        let fallbackForms = fallbackResponse.data?.forms || fallbackResponse.data?.data || [];
        if (!Array.isArray(fallbackForms) && fallbackResponse.data) {
          fallbackForms = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
        }
        forms = fallbackForms;
      }

      // Filter active forms
      const activeForms = forms.filter((f) => f.isActive !== false);

      // Filter forms that have showOnWidget === true or showInWidget === true
      let widgetForms = activeForms.filter(
        (f) => f.showOnWidget === true || f.showInWidget === true
      );

      // Fallback: If no form explicitly marked showOnWidget, show active forms
      if (widgetForms.length === 0 && activeForms.length > 0) {
        widgetForms = activeForms;
      }

      setAvailableForms(widgetForms);
      // If only 1 form is active for widget, auto-select it; if multiple, require user to select
      if (widgetForms.length === 1) {
        setSelectedForm(widgetForms[0]);
        setupCanvasForm(widgetForms[0]);
      } else {
        setSelectedForm(null);
        setCanvasSections([]);
      }
    } catch (error) {
      console.error("Error loading widget ticket forms:", error);
    } finally {
      setLoadingForms(false);
    }
  }, [widgetConfigs?.uniquePartnerId]);

  useEffect(() => {
    loadAvailableForms();
  }, [loadAvailableForms]);

  // Convert form definition to canvas sections
  const setupCanvasForm = (form) => {
    if (!form) return;
    const sections = convertFormToCanvasSections(form);
    setCanvasSections(sections);

    // Initialize default field values
    const initialData = {};
    const allFields = getAllFieldsFromSections(sections);
    allFields.forEach((field) => {
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        initialData[field.apiName] = field.defaultValue;
      } else if (field.type === "bool" || field.type === "checkbox") {
        initialData[field.apiName] = false;
      } else {
        initialData[field.apiName] = "";
      }
    });

    setFormData(initialData);
  };

  const convertFormToCanvasSections = (form) => {
    if (!form || !form.fields) {
      return getDefaultCanvasSections();
    }

    const sectionsMap = {};
    (form.sections || []).forEach((sec) => {
      sectionsMap[sec.id] = {
        id: sec.id,
        title: sec.title || "Section",
        isSystem: sec.isSystem || false,
        rows: [],
      };
    });

    if (!sectionsMap["default"]) {
      sectionsMap["default"] = {
        id: "default",
        title: "General Information",
        isSystem: true,
        rows: [],
      };
    }

    const sectionOrder =
      form.sectionOrder && form.sectionOrder.length > 0
        ? form.sectionOrder
        : Object.keys(sectionsMap);

    const sectionFields = {};
    form.fields.forEach((field) => {
      if (!field || field.enabled === false) return;

      // Skip attachments field as it is already rendered in the bottom upload card
      const apiNameLower = (field.apiName || "").toLowerCase();
      if (apiNameLower === "attachments" || apiNameLower === "attachment" || field.type === "file") return;

      const secId = field.sectionId || "default";
      const rowIdx = field.position?.row !== undefined ? field.position.row : 0;
      const colIdx = field.position?.column !== undefined ? field.position.column : 0;

      if (!sectionFields[secId]) sectionFields[secId] = {};
      if (!sectionFields[secId][rowIdx]) {
        sectionFields[secId][rowIdx] = { left: null, right: null };
      }

      // Prioritize labelForCustomer -> labelForAgent -> label -> apiName
      const displayLabel = getFieldDisplayLabel(field);

      const canvasField = {
        apiName: field.apiName,
        globalFieldId: field.globalFieldId || field._id,
        label: displayLabel,
        labelForCustomer: field.labelForCustomer || displayLabel,
        labelForAgent: field.labelForAgent || displayLabel,
        type: field.type || "string",
        options: field.options || [],
        enhancedOptions: field.enhancedOptions || [],
        disabledOptions: field.disabledOptions || [],
        dependencies: field.dependencies || null,
        isDependentField: field.isDependentField,
        isTriggerField: field.isTriggerField,
        required: field.mandatory || field.required || false,
        readOnly: false,
        placeholder:
          field.placeholderForCustomerPortal ||
          field.placeholder ||
          field.placeholderForAgentPortal ||
          "",
        defaultValue: field.defaultValue || "",
      };

      if (colIdx === 0) {
        sectionFields[secId][rowIdx].left = canvasField;
      } else {
        sectionFields[secId][rowIdx].right = canvasField;
      }
    });

    const sections = [];
    sectionOrder.forEach((secId) => {
      if (sectionsMap[secId] && sectionFields[secId]) {
        const sec = sectionsMap[secId];
        const rows = Object.values(sectionFields[secId]).filter(
          (r) => r.left || r.right
        );
        if (rows.length > 0) {
          sec.rows = rows;
          sections.push(sec);
        }
      }
    });

    return sections.length > 0 ? sections : getDefaultCanvasSections();
  };

  const getDefaultCanvasSections = () => {
    return [
      {
        id: "default",
        title: "Ticket Details",
        isSystem: true,
        rows: [
          {
            left: {
              apiName: "subject",
              label: "Subject",
              labelForCustomer: "Subject",
              type: "string",
              required: true,
              placeholder: "What is the issue regarding?",
            },
            right: null,
          },
          {
            left: {
              apiName: "description",
              label: "Description",
              labelForCustomer: "Description",
              type: "textarea",
              required: true,
              placeholder: "Please describe your issue in detail...",
            },
            right: null,
          },
        ],
      },
    ];
  };

  const getAllFieldsFromSections = (sections) => {
    const fields = [];
    sections.forEach((sec) => {
      (sec.rows || []).forEach((row) => {
        if (row.left) fields.push(row.left);
        if (row.right) fields.push(row.right);
      });
    });
    return fields;
  };

  const handleFormChange = (formId) => {
    if (!formId) {
      setSelectedForm(null);
      setCanvasSections([]);
      setFormData({});
      return;
    }
    const form = availableForms.find((f) => String(f._id) === String(formId));
    if (form) {
      setSelectedForm(form);
      setupCanvasForm(form);
    }
  };

  const handleInputChange = (apiName, value) => {
    setFormData((prev) => ({
      ...prev,
      [apiName]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
  };

  const handleDeleteAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper to parse email string into JSON array format [{ name: '', address: 'a@b.com' }]
  const parseEmailFieldToJson = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    const emails = String(val)
      .split(/[\s,]+/)
      .filter((e) => e.trim().length > 0);
    return emails.map((email) => ({
      name: email.split("@")[0] || "",
      address: email.trim(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedForm) {
      alert("Please select a Form Template before submitting.");
      return;
    }
    setLoading(true);
    const token = localStorage.getItem("dexkor-chat-widget-auth-token");
    const baseUrl = process.env.REACT_APP_CHAT_MICROSERVICE_URL || "";

    try {
      // 1. Upload Attachments to S3 if any
      let uploadedAttachments = [];
      if (attachments.length > 0) {
        const uploadPromises = attachments.map(async (file) => {
          const fileFormData = new FormData();
          fileFormData.append("attachment", file);

          const res = await fetch(`${baseUrl}/attachment`, {
            method: "POST",
            body: fileFormData,
          });

          if (!res.ok) throw new Error("File upload failed");
          const result = await res.json();
          return {
            url: result.data.url,
            filename: result.data.filename,
            type: result.data.type,
            size: result.data.size,
          };
        });

        uploadedAttachments = await Promise.all(uploadPromises);
      }

      // 2. Separate parent object fields from customFields
      const parentStandardKeys = new Set([
        "name",
        "number",
        "phone",
        "email",
        "subject",
        "title",
        "description",
        "details",
        "notes",
        "category",
        "subCategory",
        "subcategory",
        "status",
        "priority",
        "cc",
        "bcc",
        "to",
        "assignee",
        "owner",
        "type",
        "source",
        "subSource",
        "module",
        "formId",
        "formName",
        "uniquePartnerId",
        "createdBy",
        "customerCode",
        "widget_web_url",
        "messages",
        "attachments",
        "attachment",
      ]);

      const name = formData.name || customerDetails?.name || widgetConfigs?.userName || null;
      const number = formData.number || formData.phone || customerDetails?.phone || widgetConfigs?.userNumber || null;
      const email = formData.email || customerDetails?.email || widgetConfigs?.userEmail || widgetConfigs?.email || null;

      const emailVal = (email || "").trim();
      const emailPrefix = emailVal && emailVal.includes("@") ? emailVal.split("@")[0].trim() : "";
      const phoneVal = (number || "").trim();
      const userNameVal = (name || "").trim();

      const subject =
        formData.subject ||
        formData.title ||
        emailPrefix ||
        phoneVal ||
        userNameVal ||
        (selectedForm ? selectedForm.name : "New Ticket");
      const description =
        formData.description || formData.details || formData.notes || "";

      const category = formData.category || "";
      const subCategory = formData.subCategory || formData.subcategory || "";
      const status = formData.status || "unassigned";
      const priority = formData.priority || "medium";
      const assignee = formData.assignee || null;
      const owner = formData.owner || null;

      // Format CC list in JSON format
      let ccList = [];
      if (formData.cc) {
        ccList = parseEmailFieldToJson(formData.cc);
      } else if (email) {
        ccList = [
          {
            name: name || "Customer",
            address: email,
          },
        ];
      }

      // Format BCC list in JSON format
      let bccList = [];
      if (formData.bcc) {
        bccList = parseEmailFieldToJson(formData.bcc);
      }

      // Process customFields: ONLY include fields that are not parent standard keys
      const cleanedCustomFields = {};
      const allFields = getAllFieldsFromSections(canvasSections);
      allFields.forEach((field) => {
        const apiKey = field.apiName;
        if (!apiKey || parentStandardKeys.has(apiKey)) return;

        let val = formData[apiKey];
        if (val !== undefined && val !== null && val !== "") {
          if (
            field.type === "email" ||
            ["cc", "bcc", "to"].includes(apiKey)
          ) {
            val = parseEmailFieldToJson(val);
          }
          cleanedCustomFields[apiKey] = val;
        }
      });

      Object.keys(formData).forEach((key) => {
        if (!parentStandardKeys.has(key) && cleanedCustomFields[key] === undefined) {
          const val = formData[key];
          if (val !== undefined && val !== null && val !== "") {
            cleanedCustomFields[key] = val;
          }
        }
      });

      const initialMessages = [];
      if (description || uploadedAttachments.length > 0) {
        initialMessages.push({
          sender: "Customer",
          fromAddress: email,
          message: description || "",
          attachmentType: uploadedAttachments.length > 0 ? "image/jpeg" : "none",
          attachments: uploadedAttachments.map((att) => ({
            filename: att.filename,
            url: att.url,
            type: att.type,
            size: att.size,
          })),
        });
      }

      const payload = {
        uniquePartnerId: widgetConfigs.uniquePartnerId,
        createdBy: widgetConfigs?.userId,
        customerCode: widgetConfigs?.customerCode || null,
        name: name,
        number: number,
        email: email,
        subject: subject,
        description: description,
        category: category,
        subCategory: subCategory,
        status: status,
        priority: priority,
        assignee: assignee,
        owner: owner,
        cc: ccList,
        bcc: bccList,
        module: "module015",
        source: "widget", // Always keep source as "widget"
        formId: selectedForm?._id,
        formName: selectedForm?.name,
        customFields: cleanedCustomFields,
        attachments: uploadedAttachments,
        widget_web_url: window.location.href,
        messages: initialMessages,
      };

      // 3. Submit ticket
      const response = await axios.post(
        `${baseUrl}/tickets/create`,
        payload,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      setTicketId(response.data._id || response.data.data?._id);
      setLoading(false);
    } catch (error) {
      console.error("Error submitting ticket form (V2):", error);
      setLoading(false);
    }
  };

  const renderFieldInput = (field, fieldState) => {
    const value = formData[field.apiName] !== undefined ? formData[field.apiName] : "";
    const isDisabled = fieldState.disabled;
    const fieldLabel = getFieldDisplayLabel(field);

    const isUserField =
      field.type === "user" ||
      ["assignee", "owner", "createdBy", "updatedBy", "created_by", "updated_by"].includes(
        field.apiName
      );

    // If field is user type or assignee/owner, render user dropdown select
    if (isUserField) {
      const userOptions = partnerUsers.map((u) => ({
        value: u._id || u.id,
        label: u.name && u.email ? `${u.name} (${u.email})` : u.name || u.email,
      }));

      const userFieldConfig = {
        ...field,
        options: [{ value: "", label: `Select ${fieldLabel}` }, ...userOptions],
      };

      return (
        <EnhancedSelect
          field={userFieldConfig}
          value={value}
          onChange={(e) => handleInputChange(field.apiName, e.target.value)}
          disabled={isDisabled}
        />
      );
    }

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={fieldState.required}
            disabled={isDisabled}
            rows={3}
            className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all resize-none min-h-[80px] disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          />
        );

      case "select":
      case "picklist":
      case "dropdown":
      case "enum":
        return (
          <EnhancedSelect
            field={field}
            value={value}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            disabled={isDisabled}
          />
        );

      case "bool":
      case "checkbox":
        return (
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id={field.apiName}
              checked={Boolean(value)}
              disabled={isDisabled}
              onChange={(e) => handleInputChange(field.apiName, e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
            />
            <label htmlFor={field.apiName} className="text-xs font-medium text-slate-700 cursor-pointer">
              {fieldLabel}
            </label>
          </div>
        );

      case "date":
      case "datetime":
        return (
          <input
            type="date"
            value={value}
            disabled={isDisabled}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            required={fieldState.required}
            className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={value}
            disabled={isDisabled}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={fieldState.required}
            className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          />
        );

      case "email":
        return (
          <input
            type="email"
            value={value}
            disabled={isDisabled}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={fieldState.required}
            className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            disabled={isDisabled}
            onChange={(e) => handleInputChange(field.apiName, e.target.value)}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={fieldState.required}
            className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          />
        );
    }
  };

  const primaryColor =
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    setupDetails?.widgetCustomizations?.primaryColor ||
    "#1e293b";

  if (loadingForms) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <PageLoader />
      </div>
    );
  }

  // Determine row layout class: 1 column when collapsed (!isExpanded), 2 columns when expanded
  const gridRowClass = isExpanded
    ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
    : "grid grid-cols-1 gap-3";

  return (
    <div className="w-full h-full flex-1 min-h-0 bg-white flex flex-col overflow-hidden">
      {/* --- Sleek Header --- */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back to tickets"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2]" />
        </button>

        <div className="font-semibold text-slate-800 text-sm tracking-tight text-center truncate px-2 flex-1">
          {selectedForm ? selectedForm.name : "Create Ticket"}
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          {setIsExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              ) : (
                <Maximize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
              else setShowForm(false);
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* --- Main Scrollable Form Body --- */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-3.5 sm:p-5 space-y-4 dexkor-scrollbar bg-slate-50/40">
          {/* Form Selector Dropdown (only if multiple forms exist) */}
          {availableForms.length > 1 && (
            <div className="bg-white rounded-xl p-3.5 border border-slate-200/80 shadow-xs space-y-1.5">
              <label className="block text-xs font-medium text-slate-600 mb-1">
               What can we help you with? <span className="text-rose-500 font-medium">*</span>
              </label>
              <div className="relative">
                <select
                  value={selectedForm?._id || ""}
                  onChange={(e) => handleFormChange(e.target.value)}
                  className="w-full px-3 py-2 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-lg text-xs sm:text-sm font-medium text-slate-800 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all cursor-pointer pr-9"
                >
                  <option value="">-- Select a request type * --</option>
                  {availableForms.map((f) => (
                    <option key={f._id} value={f._id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Placeholder when no form is selected */}
          {!selectedForm && (
            <div className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-xs text-center space-y-2.5 my-2">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-slate-800">Select a Form Template</h4>
                <p className="text-[11px] sm:text-xs text-slate-500 max-w-xs mx-auto mt-0.5">
                  Please select a form template from the dropdown above to load the form fields.
                </p>
              </div>
            </div>
          )}

          {/* Canvas Dynamic Sections & Fields */}
          {selectedForm && canvasSections.map((sec) => (
            <div
              key={sec.id}
              className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-xs space-y-3.5"
            >
              {sec.title && (
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2 mb-3">
                  {sec.title}
                </div>
              )}

              <div className="space-y-3">
                {sec.rows.map((row, rowIdx) => {
                  const leftState = row.left
                    ? getFieldState(row.left, canvasSections, formData)
                    : { visible: false };
                  const rightState = row.right
                    ? getFieldState(row.right, canvasSections, formData)
                    : { visible: false };

                  if (!leftState.visible && !rightState.visible) return null;

                  return (
                    <div key={rowIdx} className={gridRowClass}>
                      {row.left && leftState.visible && (
                        <div
                          className={`space-y-1 ${
                            !row.right || !rightState.visible || !isExpanded
                              ? "sm:col-span-2"
                              : ""
                          }`}
                        >
                          <label className="block text-start text-xs font-medium text-slate-600 mb-1">
                            {getFieldDisplayLabel(row.left)}{" "}
                            {leftState.required && (
                              <span className="text-rose-500 font-medium ml-0.5">*</span>
                            )}
                          </label>
                          {renderFieldInput(row.left, leftState)}
                        </div>
                      )}

                      {row.right && rightState.visible && (
                        <div className="space-y-1">
                          <label className="block text-start text-xs font-medium text-slate-600 mb-1">
                            {getFieldDisplayLabel(row.right)}{" "}
                            {rightState.required && (
                              <span className="text-rose-500 font-medium ml-0.5">*</span>
                            )}
                          </label>
                          {renderFieldInput(row.right, rightState)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Attachments Upload Card */}
          {selectedForm && (
            <div className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-xs space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-start text-xs font-medium text-slate-600">
                  Attachments <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <span className="text-[11px] text-slate-400">Max 10MB</span>
              </div>

              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full py-3.5 px-3 border-2 border-slate-200 border-dashed rounded-lg cursor-pointer bg-slate-50/50 hover:bg-slate-50 hover:border-slate-300 transition-colors group">
                  <div className="flex flex-col items-center justify-center text-center">
                    <svg
                      className="w-5 h-5 mb-1 text-slate-400 group-hover:text-slate-600 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-xs font-medium text-slate-700">
                      Click to upload or drag & drop
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">PNG, JPG, PDF, or DOCX</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* Selected Attachments list */}
              {attachments.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-slate-50 border border-slate-200/80 px-3 py-1.5 rounded-lg"
                    >
                      <div className="flex items-center space-x-2 min-w-0 flex-1 pr-2">
                        <div className="w-6 h-6 flex items-center justify-center bg-indigo-50 border border-indigo-100 rounded-md shrink-0">
                          <span className="text-[10px] font-semibold text-indigo-600 uppercase">
                            {file.name.split(".").pop()?.substring(0, 3)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-slate-800 truncate">
                            {file.name}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(index)}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors border-none bg-transparent cursor-pointer shrink-0"
                        title="Remove file"
                      >
                        <IoTrashOutline className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed Bottom Action Bar */}
        <div className="p-3.5 px-4 sm:px-5 border-t border-slate-200 flex-shrink-0 bg-white flex items-center justify-end gap-2.5 shadow-sm">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedForm}
            className="px-5 py-2 text-xs sm:text-sm font-medium text-white rounded-lg shadow-sm hover:opacity-95 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-none cursor-pointer flex items-center justify-center gap-2"
            style={{
              background: primaryColor,
            }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating...
              </span>
            ) : (
              "Create Ticket"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IssueFormV2;
